import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Megaphone, X } from "lucide-react";
import { EYBLogo } from "@/components/EYBLogo";
import { FolderTile } from "@/components/FolderTile";
import { FolderOverlay } from "@/components/FolderOverlay";
import { InAppBrowser } from "@/components/InAppBrowser";
import { ContactCard } from "@/components/ContactCard";
import { InstallPrompt } from "@/components/InstallPrompt";
import { AnnouncementsSheet } from "@/components/AnnouncementsSheet";
import { getActiveAnnouncements } from "@/lib/api";

export default function Home() {
  const [folderOpen, setFolderOpen] = useState(false);
  const [activeApp, setActiveApp] = useState(null);
  const [announcements, setAnnouncements] = useState([]);
  const [sheetOpen, setSheetOpen] = useState(false);
  const [bannerDismissed, setBannerDismissed] = useState(false);

  useEffect(() => {
    getActiveAnnouncements()
      .then(setAnnouncements)
      .catch(() => {});
  }, []);

  const latest = announcements[0];
  const seenId = localStorage.getItem("eyb_ann_seen");
  const hasUnseen = latest && latest.id !== seenId;
  const bannerHidden =
    bannerDismissed ||
    !latest ||
    localStorage.getItem("eyb_ann_banner") === latest.id;

  const openSheet = () => {
    setSheetOpen(true);
    if (latest) localStorage.setItem("eyb_ann_seen", latest.id);
  };

  const dismissBanner = () => {
    if (latest) localStorage.setItem("eyb_ann_banner", latest.id);
    setBannerDismissed(true);
  };

  return (
    <div
      data-testid="eyb-app"
      className="relative mx-auto flex h-[100dvh] w-full max-w-md flex-col overflow-hidden"
    >
      <div className="pointer-events-none absolute -left-16 top-10 h-56 w-56 rounded-full bg-[#1B9DE0]/25 blur-3xl" />
      <div className="pointer-events-none absolute -right-20 bottom-24 h-64 w-64 rounded-full bg-[#0B4C8C]/40 blur-3xl" />

      {/* Announcements bell */}
      <button
        type="button"
        onClick={openSheet}
        data-testid="announcements-bell"
        aria-label="Announcements"
        className="absolute right-5 top-6 z-20 grid h-11 w-11 place-items-center rounded-full border border-white/15 bg-white/10 text-white backdrop-blur-md active:scale-90"
      >
        <Megaphone size={18} />
        {hasUnseen && (
          <span
            data-testid="announcements-dot"
            className="absolute right-2 top-2 h-2.5 w-2.5 rounded-full bg-[#38D6B5] ring-2 ring-[#0b3566]"
          />
        )}
      </button>

      {/* Header */}
      <motion.header
        className="relative z-10 px-7 pt-16 text-center"
        initial={{ opacity: 0, y: -16 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      >
        <div className="flex justify-center">
          <EYBLogo size={78} />
        </div>
        <motion.h1
          className="font-heading mt-6 text-2xl font-semibold leading-tight tracking-tight text-white"
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.15, duration: 0.6 }}
          data-testid="welcome-title"
        >
          Welcome to EYB Consulting
        </motion.h1>
        <motion.p
          className="mx-auto mt-2 max-w-[280px] text-sm leading-relaxed text-white/60"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.35, duration: 0.6 }}
        >
          Clarity and control, built for your business.
        </motion.p>
      </motion.header>

      {/* Announcement banner */}
      <AnimatePresence>
        {!bannerHidden && (
          <motion.button
            type="button"
            onClick={openSheet}
            data-testid="announcement-banner"
            initial={{ opacity: 0, y: -10, height: 0 }}
            animate={{ opacity: 1, y: 0, height: "auto" }}
            exit={{ opacity: 0, height: 0 }}
            className="relative z-10 mx-auto mt-5 flex w-[calc(100%-3rem)] max-w-[340px] items-start gap-3 rounded-2xl border border-white/15 bg-white/10 p-3 text-left backdrop-blur-md"
          >
            <span className="mt-0.5 grid h-8 w-8 shrink-0 place-items-center rounded-xl bg-gradient-to-b from-[#2EC1F0] to-[#1273C4]">
              <Megaphone size={15} className="text-white" />
            </span>
            <span className="min-w-0 flex-1">
              <span className="block text-sm font-semibold text-white">
                {latest.title}
              </span>
              <span className="line-clamp-2 block text-xs text-white/60">
                {latest.body}
              </span>
            </span>
            <span
              onClick={(e) => {
                e.stopPropagation();
                dismissBanner();
              }}
              data-testid="banner-dismiss"
              className="grid h-6 w-6 shrink-0 place-items-center rounded-full bg-white/10 text-white/60"
            >
              <X size={13} />
            </span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* Body */}
      <div className="relative z-10 flex flex-1 flex-col items-center justify-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.85 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.45, type: "spring", stiffness: 220, damping: 20 }}
        >
          <FolderTile onOpen={() => setFolderOpen(true)} />
        </motion.div>
        <motion.p
          className="mt-6 max-w-[260px] text-center text-xs text-white/40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.7 }}
        >
          Tap the folder to open your tools
        </motion.p>

        <div className="mt-10 w-full px-6">
          <ContactCard />
        </div>
      </div>

      <div className="relative z-10 pb-8" />

      <FolderOverlay
        open={folderOpen}
        onClose={() => setFolderOpen(false)}
        onOpenApp={setActiveApp}
      />
      <InAppBrowser app={activeApp} onClose={() => setActiveApp(null)} />
      <AnnouncementsSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        announcements={announcements}
      />
      <InstallPrompt />
    </div>
  );
}
